import {
  GraduationCap,
  Heart,
  ChefHat,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

export default function About() {
  const [currentCertificate, setCurrentCertificate] = useState(0);
  const [desktopCertificateIndex, setDesktopCertificateIndex] = useState(0);

  const certificates = [
    {
      img: "/certificates/REGISTRANT_23159_13_Jan_2026_page-0001.png",
      title: "Nutritional Therapy Qualification",
    },
    {
      img: "/certificates/Bant Certificate_page-0001.png",
      title: "BANT Membership",
    },
    {
      img: "/certificates/2026-01-03 16-42_page-0001.png",
      title: "Professional Certification",
    },
    {
      img: "/certificates/Aleksandra Jakimovska-Amanovic-Level 2 Food Hygiene and Safety for Catering_page-0001.png",
      title: "Level 2 Food Hygiene and Safety for Catering",
    },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-b bg-primary to-green-400/10">
      <Helmet>
        <title>
          About Aleks - BSc (Hons) Nutritional Therapist | regCNHC, mBANT
        </title>
        <meta
          name="description"
          content="Meet Aleks, a First Class Degree Nutritional Therapist (BSc(Hons), regCNHC, mBANT) specializing in personalised nutrition therapy. Learn about her 20+ years passion for nutrition, recovery journey, and expertise in traditional cooking and modern nutrition."
        />
        <meta
          name="keywords"
          content="nutritional therapist, nutrition therapy, registered CNHC, BANT member, personalised nutrition, health coaching"
        />
        <meta
          property="og:title"
          content="About Aleks - Registered Nutritional Therapist"
        />
        <meta
          property="og:description"
          content="Discover Aleks's journey from overcoming an eating disorder to becoming a certified nutritional therapist with a First Class Degree. Learn her expertise and credentials."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
        {/* Page Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            About Me
          </h1>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start mb-12">
          {/* Image Section */}
          <div className="lg:col-span-1">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-green-400/50 rounded-3xl transform rotate-3"></div>
              <img
                src="/sashka1.jpg"
                alt="Aleksandra - Nutritional Therapist"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
            <div className="text-center mt-4 text-[#F8F9FA]">
              BSc (Hons), regCNHC, mBANT
            </div>
          </div>

          {/* Text Section */}
          <div className="lg:col-span-2">
            {/* Single Combined Text Section */}
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-10 lg:p-12 space-y-8">
              <div className="prose prose-lg max-w-none">
                <div className="mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 pb-4 border-b-2 border-primary/20">
                    My Story
                  </h2>
                  <p className="text-base sm:text-lg text-left text-gray-700 leading-relaxed mb-6">
                    My name is Aleksandra, and I live in Ealing, West London. I
                    have been married to my wonderful partner for over 20 years,
                    and we share two teenage daughters and a miniature
                    schnauzer. Although I have had a lifetime passion for
                    nutrition, I spent most of my teenage and early adult years
                    battling an eating disorder. It took me ten years to recover
                    by learning how to explore my personal circumstances, root
                    causes and triggers, self-compassion, and intrinsic factors.
                    Learning how to love and accept myself was one of the most
                    challenging tasks on my healing journey. The diet and
                    lifestyle adaptations naturally followed, but being in a
                    stable and loving relationship made it all happen.
                  </p>
                  <p className="text-base sm:text-lg text-left text-gray-700 leading-relaxed mb-6">
                    My life came to another crossroad when my husband was
                    diagnosed with a challenging condition in 2016. It felt like
                    everything came crashing down, and I was staring at a
                    potential future as a single parent. Luckily, my husband's
                    surgery was a success, and we could start on a different
                    kind of recovery journey. It was full of challenges, but
                    also full of different perspectives, positive ones. I became
                    very inquisitive about how to improve his recovery and
                    researched for hours almost every night. It became my
                    mission. Secondly, I decided to take my health even further
                    by improving my diet and taking up running. The running felt
                    truly transformative (I later found out there is a science
                    behind it) and the idea to sign up for the nutritional
                    therapy course at the University of West London was born.
                    Five years later, I graduated with a First Class Degree. It
                    felt like a very long marathon, but I made it to the finish
                    line.
                  </p>
                  <p className="text-base sm:text-lg text-left text-gray-700 leading-relaxed">
                    Homemade food has always been a non-negotiable priority for
                    me due to my heritage, and I wear it as a badge of honor. I
                    love traditional cooking with a bit of a twist to match my
                    family’s nutritional needs. Working as a Childminder in my
                    previous job in order to raise my children has given me the
                    amazing opportunity to cook nutritional meals every day, a
                    privilege not everyone can afford. Now, paired with my
                    nutrition degree, it would be a privilege to share my
                    passion for cooking with you, my client.
                  </p>{" "}
                </div>
                {/* Key Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t-2 border-primary/20">
                  <div className="text-center p-4 bg-gradient-to-br from-primary/10 to-green-50 rounded-xl">
                    <div className="flex justify-center mb-2">
                      <GraduationCap className="w-10 h-10 text-primary" />
                    </div>
                    <p className="font-bold text-gray-900">
                      First Class Degree
                    </p>
                    <p className="text-sm text-gray-600">
                      University of West London
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-primary/10 to-green-50 rounded-xl">
                    <div className="flex justify-center mb-2">
                      <Heart className="w-10 h-10 text-red-500 fill-red-500" />
                    </div>
                    <p className="font-bold text-gray-900">20+ Years</p>
                    <p className="text-sm text-gray-600">
                      Passion for Nutrition
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-primary/10 to-green-50 rounded-xl">
                    <div className="flex justify-center mb-2">
                      <ChefHat className="w-10 h-10 text-green-700" />
                    </div>
                    <p className="font-bold text-gray-900">Cooking Expert</p>
                    <p className="text-sm text-gray-600">
                      Traditional & Modern
                    </p>
                  </div>
                </div>{" "}
              </div>
            </div>
          </div>
        </div>

        {/* Second Image Section */}
        {/* <div className="mb-12 md:mb-16">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-green-400/50 rounded-3xl transform -rotate-3"></div>
            <img
              src="/sashka2.jpg"
              alt="Aleksandra - Nutritional Therapist"
              className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
            />
          </div>
        </div> */}

        {/* Call to Action */}
        <div className="mt-12 md:mt-16 text-center">
          {/* <div className="bg-gradient-to-r from-white/35 to-green-200/10 rounded-2xl p-8 sm:p-12"> */}
          <div className="bg-white/35 rounded-2xl p-8 sm:p-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to start your health journey?
            </h3>
            <p className="text-base sm:text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
              Book a consultation today and take the first step towards a
              healthier, happier you.
            </p>
            <button
              onClick={() =>
                window.open(
                  "https://www.bookable.page/aleksandra",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="bg-primary hover:bg-[#245940] text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-base sm:text-lg"
            >
              Book Consultation
            </button>
          </div>
        </div>

        {/* Certificates Section */}
        <div className="mt-16 md:mt-24">
          <div className="space-y-3 mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center">
              My Credentials
            </h2>
            <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div className="relative overflow-hidden mb-6">
              <div
                className="flex transition-transform duration-300"
                style={{
                  transform: `translateX(-${currentCertificate * 100}%)`,
                }}
              >
                {certificates.map((cert, index) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                  >
                    <img src={cert.img} className="w-full" alt={cert.title} />
                    <div className="p-4 text-center">
                      <p className="text-gray-600 font-semibold text-sm">
                        {cert.title}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel Controls */}
            <div className="flex items-center justify-between">
              <button
                onClick={() =>
                  setCurrentCertificate((prev) =>
                    prev === 0 ? certificates.length - 1 : prev - 1,
                  )
                }
                className="bg-white/95 text-primary p-2 rounded-full shadow-lg hover:shadow-xl hover:bg-white transition-all duration-300"
                aria-label="Previous certificate"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex gap-2">
                {certificates.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentCertificate(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentCertificate
                        ? "bg-white w-6"
                        : "bg-white/50 w-2"
                    }`}
                    aria-label={`Go to certificate ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={() =>
                  setCurrentCertificate((prev) =>
                    prev === certificates.length - 1 ? 0 : prev + 1,
                  )
                }
                className="bg-white/95 text-primary p-2 rounded-full shadow-lg hover:shadow-xl hover:bg-white transition-all duration-300"
                aria-label="Next certificate"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Desktop Carousel */}
          <div className="hidden md:block">
            <div className="relative">
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-300 gap-6"
                  style={{
                    transform: `translateX(-${desktopCertificateIndex * 33.33}%)`,
                  }}
                >
                  {certificates.map((cert, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300"
                      style={{ width: "calc(30% - 16px)" }}
                    >
                      <img src={cert.img} alt={cert.title} className="w-full" />
                      <div className="p-4 text-center">
                        <p className="text-gray-600 font-semibold text-sm">
                          {cert.title}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              {desktopCertificateIndex > 0 && (
                <button
                  onClick={() =>
                    setDesktopCertificateIndex((prev) => Math.max(0, prev - 1))
                  }
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white/95 text-primary p-3 rounded-full shadow-lg hover:shadow-xl hover:bg-white transition-all duration-300 z-10"
                  aria-label="Previous certificates"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}

              {desktopCertificateIndex < certificates.length - 3 && (
                <button
                  onClick={() =>
                    setDesktopCertificateIndex((prev) =>
                      Math.min(certificates.length - 3, prev + 1),
                    )
                  }
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white/95 text-primary p-3 rounded-full shadow-lg hover:shadow-xl hover:bg-white transition-all duration-300 z-10"
                  aria-label="Next certificates"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* Dot Indicators */}
            <div className="flex gap-2 justify-center mt-6">
              {Array.from({ length: certificates.length - 2 }).map(
                (_, index) => (
                  <button
                    key={index}
                    onClick={() => setDesktopCertificateIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === desktopCertificateIndex
                        ? "bg-white w-6"
                        : "bg-white/50 w-2"
                    }`}
                    aria-label={`Go to certificate group ${index + 1}`}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
